import 'react-simple-keyboard/build/css/index.css'

import { useState } from 'react'
import KeyboardSimple from 'react-simple-keyboard'

const DEFAULT_LAYOUT = {
    default: [
        '` 1 2 3 4 5 6 7 8 9 0 - = {backspace}',
        '{tab} q w e r t y u i o p [ ] \\',
        "{lock} a s d f g h j k l ; ' {enter}",
        '{shift} z x c v b n m , . / {shift}',
        '.com @ {space}'
    ],
    shift: [
        '~ ! @ # $ % ^ & * ( ) _ + {backspace}',
        '{tab} Q W E R T Y U I O P { } |',
        '{lock} A S D F G H J K L : " {enter}',
        '{shift} Z X C V B N M < > ? {shift}',
        '.com @ {space}'
    ]
}

/** Serbian Latin layout with Š Đ Č Ć Ž, fewer keys per row for wider touch targets. */
const SERBIAN_LAYOUT = {
    default: [
        '1 2 3 4 5 6 7 8 9 0 {backspace}',
        'q w e r t y u i o p',
        'a s d f g h j k l',
        '{shift} z x c v b n m , .',
        'š đ č ć ž {space} {enter}'
    ],
    shift: [
        '! ? # % & * ( ) - + {backspace}',
        'Q W E R T Y U I O P',
        'A S D F G H J K L',
        '{shift} Z X C V B N M ; :',
        'Š Đ Č Ć Ž {space} {enter}'
    ]
}

const SERBIAN_DISPLAY = {
    '{backspace}': '⌫',
    '{enter}': 'OK',
    '{shift}': '⇧',
    '{space}': 'Razmak'
}

export type KeyboardVariant = 'default' | 'serbian'
export type KeyboardPlacement = 'fixed' | 'inline'

export type KeyboardProps = {
    value?: string
    onChangeValue?: (value: string) => void
    onChange?: (value: string) => void
    onUpdateFocus: (value: boolean) => void
    variant?: KeyboardVariant
    placement?: KeyboardPlacement
    className?: string
}

export default function Keyboard({
    onChangeValue,
    onChange,
    onUpdateFocus,
    value,
    variant = 'default',
    placement = 'fixed',
    className = ''
}: KeyboardProps) {
    const [layoutName, setLayoutName] = useState('default')
    const isSerbian = variant === 'serbian'

    const onKeyPress = (button: string) => {
        if (button === '{enter}') {
            onUpdateFocus(false)
            return
        }

        if (button === '{shift}' || button === '{lock}') {
            setLayoutName(prev => (prev === 'default' ? 'shift' : 'default'))
            return
        }

        if (button === '{space}') {
            if (onChange) {
                onChange((value || '') + ' ')
            }
            if (isSerbian && layoutName === 'shift') {
                setLayoutName('default')
            }
            return
        }

        if (!['{backspace}', '{tab}'].includes(button)) {
            if (onChange) {
                onChange((value || '') + button)
            }
            if (isSerbian && layoutName === 'shift') {
                setLayoutName('default')
            }
            return
        }

        if (button === '{backspace}' && value && value.length > 0) {
            if (onChange) {
                onChange(value.slice(0, -1))
            }
        }
    }

    const containerClassName = [
        'keyboard-container',
        `keyboard-container--${placement}`,
        isSerbian ? 'keyboard-container--serbian' : '',
        className
    ]
        .filter(Boolean)
        .join(' ')

    return (
        <div className={containerClassName}>
            <KeyboardSimple
                layoutName={layoutName}
                onChange={onChangeValue}
                onKeyPress={onKeyPress}
                layout={isSerbian ? SERBIAN_LAYOUT : DEFAULT_LAYOUT}
                display={isSerbian ? SERBIAN_DISPLAY : undefined}
                buttonTheme={
                    isSerbian
                        ? [
                              {
                                  class: 'hg-serbian-letter',
                                  buttons: 'š đ č ć ž Š Đ Č Ć Ž'
                              },
                              {
                                  class: 'hg-function-key',
                                  buttons: '{backspace} {enter} {shift} {space}'
                              }
                          ]
                        : undefined
                }
                buttonAttributes={[
                    {
                        attribute: 'aria-label',
                        value: 'Change to uppercase',
                        buttons: '{shift} {lock}'
                    }
                ]}
            />
        </div>
    )
}
